import { Stack } from "@mui/material";
import { LoadingOrEmptyWrapper } from "components/LoadingAndEmptyHandler";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { cn } from "utils/styling";

import type {
  RecordFormColumns,
  RecordFormField,
  RecordFormTab,
  RecordFormTabOrientation,
  RecordFormTabPanelPlacement,
} from "./types";

import { DynamicRecordDrawerContent } from "./DynamicRecordDrawerContent";
import { DynamicRecordDrawerFooter } from "./DynamicRecordDrawerFooter";
import { DynamicRecordDrawerHeader } from "./DynamicRecordDrawerHeader";
import { getTabsWithVisibleFields } from "./lib/layout";

export type DynamicRecordFormShellProps = {
  children?: ReactNode;
  className?: string;
  columns?: RecordFormColumns;
  fields?: RecordFormField[];
  headerActions?: ReactNode;
  headerColumns?: RecordFormColumns;
  hideFooter?: boolean;
  isDeleting?: boolean;
  isLoading?: boolean;
  isSubmitting?: boolean;
  mode?: "add" | "edit";
  onCancel?: () => void;
  onClose: () => void;
  onDelete?: () => void;
  onDeleteLabel?: string;
  onSave?: () => void;
  subtitle?: string;
  tabOrientation?: RecordFormTabOrientation;
  tabPanelPlacement?: Partial<Record<string, RecordFormTabPanelPlacement>>;
  tabPanels?: Partial<Record<string, ReactNode>>;
  tabs?: RecordFormTab[];
  title: string;
};

export const DynamicRecordFormShell = ({
  children,
  className,
  columns = 2,
  fields = [],
  headerActions,
  headerColumns,
  hideFooter = false,
  isDeleting,
  isLoading,
  isSubmitting,
  mode = "edit",
  onCancel,
  onClose,
  onDelete,
  onDeleteLabel,
  onSave,
  subtitle,
  tabOrientation = "horizontal",
  tabPanelPlacement,
  tabPanels,
  tabs = [],
  title,
}: DynamicRecordFormShellProps) => {
  const visibleTabs = useMemo(() => getTabsWithVisibleFields(fields, tabs), [fields, tabs]);
  const [activeTabName, setActiveTabName] = useState(visibleTabs[0]?.name ?? "");

  useEffect(() => {
    const activeTab = visibleTabs.find((tab) => tab.name === activeTabName);
    if (!activeTab || activeTab.disabled) {
      setActiveTabName(visibleTabs.find((tab) => !tab.disabled)?.name ?? "");
    }
  }, [activeTabName, visibleTabs]);

  const resolvedOrientation = tabOrientation ? tabOrientation : visibleTabs.length > 5 ? "vertical" : "horizontal";

  const handleCancel = () => {
    onCancel?.();
    onClose();
  };

  return (
    <Stack className={cn("flex h-full min-h-0 w-full min-w-0 max-w-full flex-col overflow-hidden", className)}>
      <Stack className="w-full max-w-full shrink-0 border-b border-lds-border-subtle px-2 pb-3">
        <DynamicRecordDrawerHeader actions={headerActions} onClose={onClose} subtitle={subtitle} title={title} />
      </Stack>
      <LoadingOrEmptyWrapper height="100%" showLoading={isLoading}>
        {children ? (
          <Stack className="min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-hidden px-4 py-4">
            <div className="flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-hidden">{children}</div>
          </Stack>
        ) : (
          <DynamicRecordDrawerContent
            activeTabName={activeTabName}
            columns={columns}
            fields={fields}
            headerColumns={headerColumns}
            onTabChange={setActiveTabName}
            tabOrientation={resolvedOrientation}
            tabPanelPlacement={tabPanelPlacement}
            tabPanels={tabPanels}
            visibleTabs={visibleTabs}
          />
        )}
      </LoadingOrEmptyWrapper>
      {!hideFooter && onSave && (
        <DynamicRecordDrawerFooter
          isDeleting={isDeleting}
          isSubmitting={isSubmitting}
          mode={mode}
          onCancel={handleCancel}
          onDelete={onDelete}
          onDeleteLabel={onDeleteLabel}
          onSave={onSave}
        />
      )}
    </Stack>
  );
};
